import { Component, OnInit, inject } from '@angular/core'
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router'
import { ActivityEventTypes, FeatureAPIService, IBvFeature } from '@modules'
import { Store } from '@ngrx/store'
import { ActivityNotificationSocket, AuthorizationService, NavigationService } from '@services'
import { getHasDispatch, getLayers, getOrganizationUsers, getUser, isInventoryEnabled } from '@ziptility/store'
import { firstValueFrom, Subject } from 'rxjs'
import { switchMap, takeUntil, filter as rxFilter, take } from 'rxjs/operators'
import { AssetProfileService } from './asset-profile.service'
import { CommonModule } from '@angular/common'
import { AssetProfileHeaderComponent } from './asset-profile-header.component'
import { exportFeatures } from '@ziptility/store/export/actions'
import { EditorStore } from '@ziptility/editor'

@Component({
  selector: 'feature-details-container',
  template: `
    <div
      class="fixed bg-black bg-opacity-60 w-screen h-screen z-[200] cursor-pointer"
      (click)="onBackgroundClick($event, background)"
      #background
    >
      <div
        class="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6/12 h-screen z-50 bg-white cursor-default"
      >
        <div class="flex flex-col h-screen">
          <panel-header
            [feature]="vm.feature()"
            [systemId]="vm.systemId()"
            [layerTitle]="vm.layerTitle()"
            [locationIdentifier]="vm.locationIdentifier()"
            [mapPreviewUrl]="vm.previewImageUrl()"
            [numOpenTasks]="vm.numOpenTasks()"
            [hasDispatch]="hasDispatch$ | async"
            [hasInventory]="hasInventory$ | async"
            [canEditFeature]="vm.canEditFeature()"
            (onBeginTask)="beginTask()"
            (onMove)="move()"
            (onClose)="close()"
            (onDelete)="deleteFeature()"
            (onDuplicate)="onDuplicate()"
            (onExport)="onExport($event)"
            (onIdentifiersChange)="vm.onIdentifiersChange($event)"
          ></panel-header>
          <div class="overflow-auto h-full">
            <router-outlet></router-outlet>
          </div>
        </div>
      </div>
    </div>
  `,
  imports: [CommonModule, RouterOutlet, AssetProfileHeaderComponent],
})
export class AssetProfileComponent implements OnInit {
  readonly route = inject(ActivatedRoute)
  readonly store = inject(Store)
  readonly authorizationService = inject(AuthorizationService)
  readonly featureService = inject(FeatureAPIService)
  readonly navigationService = inject(NavigationService)
  readonly vm = inject(AssetProfileService)
  readonly router = inject(Router)
  readonly socket = inject(ActivityNotificationSocket)
  readonly editorStore = inject(EditorStore)

  users$ = this.store.select(getOrganizationUsers)
  layers$ = this.store.select(getLayers)
  hasInventory$ = this.store.select(isInventoryEnabled)
  hasDispatch$ = this.store.select(getHasDispatch)
  user$ = this.store.select(getUser)
  onDestroy$ = new Subject<void>()

  async ngOnInit() {
    await firstValueFrom(
      this.layers$.pipe(
        rxFilter((arr) => arr.length > 0),
        take(1),
      ),
    )

    this.route.params
      .pipe(
        switchMap(({ organizationId, mapId, featureId }) => this.load({ organizationId, mapId, featureId })),
        takeUntil(this.onDestroy$),
      )
      .subscribe()
  }

  async load({ organizationId, mapId, featureId }: { organizationId: string; mapId: string; featureId: string }) {
    const hasDispatch = await firstValueFrom(this.hasDispatch$)
    const hasInventory = await firstValueFrom(this.hasInventory$)
    const user = await firstValueFrom(this.user$)

    this.vm.load({ userId: user.id, organizationId, mapId, featureId, hasDispatch, hasInventory })

    this.socket
      .watch(ActivityEventTypes.comment.created)
      .pipe(rxFilter((n) => n.featureId === featureId))
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.vm.loadComments(featureId))
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  onBackgroundClick($event: MouseEvent, backgroundEl: HTMLElement) {
    if ($event.target == backgroundEl) {
      this.close()
    }
  }

  beginTask() {
    this.router.navigate(['./', 'tasks', 'create'], {
      relativeTo: this.route,
      queryParams: {
        taskTemplateId: undefined,
        featureId: this.vm.feature().id,
      },
    })
  }

  async deleteFeature() {
    await this.vm.deleteFeature()
    this.close()
  }

  onDuplicate() {
    const { type, layerId, geometry, properties } = this.vm.feature()
    const duplicateFeature = {
      layerId,
      geometry,
      properties,
      type,
    }
    this.editorStore.createFeature(duplicateFeature as IBvFeature)
  }

  async onExport(type) {
    const { id } = this.vm.feature()
    const { mapId } = await firstValueFrom(this.route.params)
    this.store.dispatch(exportFeatures({ mapId, ids: [id], format: type }))
  }

  async move() {
    this.navigationService.navigateToSelectedMap({
      queryParams: {
        move: this.vm.feature().id,
      },
    })
  }

  async close() {
    this.router.navigate(['../../'], { relativeTo: this.route })
  }
}
